import axios from 'axios';
import logger from '../utils/logger';

// Create an axios instance with default config
const api = axios.create({
  // Use environment variable for API URL
  baseURL: `${process.env.REACT_APP_API_URL}/api`,
  timeout: 10000, // 10 second timeout to prevent hanging
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Add a request interceptor to attach the auth token to every request
api.interceptors.request.use(
  (config) => {
    logger.debug('%c API REQUEST', 'background: #10B981; color: white; padding: 2px 6px; border-radius: 4px;');
    logger.debug('URL:', config.url);
    logger.debug('Method:', config.method.toUpperCase());
    logger.debug('Params:', config.params || 'None');
    
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      logger.debug('Authorization header added');
    } else {
      logger.warn('No token found in localStorage');
    }
    
    return config;
  },
  (error) => {
    logger.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle common errors
api.interceptors.response.use(
  (response) => {
    logger.debug('%c API RESPONSE SUCCESS', 'background: #10B981; color: white; padding: 2px 6px; border-radius: 4px;');
    logger.debug('URL:', response.config.url);
    logger.debug('Status:', response.status);
    logger.debug('Status Text:', response.statusText);
    logger.debug('Response Data:', response.data);
    return response;
  },
  (error) => {
    logger.error('%c API RESPONSE ERROR', 'background: #EF4444; color: white; padding: 2px 6px; border-radius: 4px;');
    
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      logger.error(`API Error ${error.response.status}: ${error.response.statusText}`, {
        endpoint: error.config.url,
        method: error.config.method,
        status: error.response.status,
        data: error.response.data
      });
      
      // Handle 401 Unauthorized errors (token expired or invalid)
      if (error.response.status === 401) {
        console.warn('401 Unauthorized - Checking if this is a new sign-up');
        
        // Check if this is a new sign-up by looking at the localStorage flag
        const isNewSignUp = localStorage.getItem('recentSignUp') === 'true';
        
        if (isNewSignUp) {
          console.log('New sign-up detected, attempting to refresh authentication');
          
          // Try to get current token and user
          const token = localStorage.getItem('token');
          const user = localStorage.getItem('user');
          
          if (token && user) {
            console.log('Token and user found in localStorage, not redirecting yet');
            // Don't redirect immediately, give the user a chance to complete the flow
            return Promise.reject(error);
          } else {
            console.warn('No token or user found, redirecting to login');
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('recentSignUp');
            window.location.href = '/login';
          }
        } else {
          console.warn('Not a new sign-up, redirecting to login');
          // Clear local storage and redirect to login
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('recentSignUp');
          window.location.href = '/login';
        }
      }
    } else if (error.request) {
      // The request was made but no response was received
      logger.error('Network error: No response received from server', {
        url: error.config.url,
        method: error.config.method
      });
    } else {
      // Something happened in setting up the request that triggered an Error
      logger.error(`API request setup error: ${error.message}`, {
        url: error.config?.url,
        method: error.config?.method
      });
    }
    return Promise.reject(error);
  }
);

export default api;
